import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { loginSuccess } from "../features/auth/authSlice";
import { updateDefaultLocationRequest } from "../api/authAPI";
import { getAccessToken, getSessionExpiry } from "../Util/authStorage.js";
import catchAxiosError from "../Util/catchAxiosError.js";

/**
 * The signed-in user's default location (users.default_location_id), which
 * every page's location selector starts on (see useUserLocations).
 *
 * Returns { userId, defaultLocationId, setDefaultLocation, patchDefaultLocation }:
 *   setDefaultLocation(id|null)   saves via PUT /login/default-location
 *                                 (optimistic, reverts on failure)
 *   patchDefaultLocation(id|null) updates the persisted session only — for
 *                                 when an admin saved it on the Users form
 */
export default function useDefaultLocation() {
    const dispatch = useDispatch();
    const user = useSelector((s) => s.auth.user);
    const defaultLocationId = user?.defaultLocationId ?? null;

    const patchDefaultLocation = useCallback(
        (id) => {
            if (!user) return;
            dispatch(loginSuccess({
                token: getAccessToken(),
                refreshExpiresAt: getSessionExpiry(),
                user: { ...user, defaultLocationId: id ?? null },
            }));
        },
        [dispatch, user],
    );

    const setDefaultLocation = useCallback(
        async (id) => {
            if (!user) return;
            const prev = user.defaultLocationId ?? null;
            patchDefaultLocation(id);
            try {
                await updateDefaultLocationRequest(id);
                toast.success("Default location saved");
            } catch (err) {
                patchDefaultLocation(prev);
                catchAxiosError(err);
            }
        },
        [user, patchDefaultLocation],
    );

    return { userId: user?.id ?? null, defaultLocationId, setDefaultLocation, patchDefaultLocation };
}
